import React from 'react';

import {
  isValidColor,
  parseColor,
} from '@packages/systems/core/utils/ColorUtils';
import TextInput from '../TextInput';

const INPUT_FOCUSED = 'INPUT_FOCUSED';
const INPUT_BLURRED = 'INPUT_BLURRED';
const INPUT_VALUE_CHANGED = 'INPUT_VALUE_CHANGED';
const VALID_VALUE_ENTERED = 'VALID_VALUE_ENTERED';
const INVALID_VALUE_ENTERED = 'INVALID_VALUE_ENTERED';

type Action =
  | {
      type: typeof INPUT_FOCUSED;
    }
  | {
      type: typeof INPUT_BLURRED;
      payload: {
        value: string;
      };
    }
  | {
      type: typeof VALID_VALUE_ENTERED;
      payload: {
        value: string;
      };
    }
  | {
      type: typeof INVALID_VALUE_ENTERED;
      payload: {
        value: string;
      };
    }
  | {
      type: typeof INPUT_VALUE_CHANGED;
      payload: {
        value: string;
      };
    };

type State = {
  focus: boolean;
  invalid: boolean;
  value?: string | null;
  _value?: string | null;
  _formattedValue?: string | null;
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case INPUT_FOCUSED: {
      return {...state, focus: true};
    }

    case INPUT_BLURRED: {
      const {
        payload: {value},
      } = action;
      return {...state, focus: false, invalid: false, value};
    }

    case VALID_VALUE_ENTERED: {
      const {
        payload: {value},
      } = action;
      return {...state, invalid: false, value};
    }

    case INVALID_VALUE_ENTERED: {
      const {
        payload: {value},
      } = action;
      return {...state, invalid: true, value};
    }

    case INPUT_VALUE_CHANGED: {
      const {
        payload: {value},
      } = action;

      return {...state, value};
    }
  }

  return state;
};

type Props = {
  value?: string;
  allowEmpty?: boolean;
  onColorChange: any;
  placeholder?: string;
  formatValue?: (value: string) => string;
};

const formatColorValue = (value: string, props: Props) =>
  (props.formatValue || parseColor)(value) || value;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const enhanceInput = <TProps extends unknown>(
  Base: (props: TProps) => any
) =>
  class EnhancedColorInput extends React.PureComponent<Props & TProps, State> {
    state = {
      ...EnhancedColorInput.getDerivedStateFromProps(this.props),
      focus: false,
      invalid: false,
    };
    dispatch = (action: Action) => {
      this.setState(reducer(this.state, action));
    };
    onFocus = () => {
      this.dispatch({type: INPUT_FOCUSED});
    };
    onBlur = () => {
      const {onColorChange, allowEmpty} = this.props;
      const {_formattedValue: oldValue} = this.state;
      let {value: newValue} = this.state;
      const trimmedValue = newValue && newValue.trim();

      if (allowEmpty && trimmedValue === '') {
        if (onColorChange) {
          onColorChange(trimmedValue);
        }
      } else if (oldValue !== newValue && newValue && isValidColor(newValue)) {
        // parse color to cast as color when newValue is something like "555" (which is #555)
        if (onColorChange) {
          onColorChange(formatColorValue(newValue, this.props));
        }
      } else {
        // if invalid, we'll need to reset the state value to the prop
        // value
        newValue = oldValue && formatColorValue(oldValue, this.props);
      }

      // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
      this.dispatch({type: INPUT_BLURRED, payload: {value: newValue}});
    };

    static getDerivedStateFromProps(props: Props, state?: State) {
      if (!state || props.value !== state._value) {
        const formattedValue =
          props.value && formatColorValue(props.value, props);
        return {
          ...state,
          _value: props.value,
          _formattedValue: formattedValue,

          // Parse incomming color value to _prettify_ it in case it's
          // something like hsla(0,0,0,1) which is set by the color picker.
          value: formattedValue,
          invalid: false,
        };
      }

      return null;
    }

    onChange = ({target}: any) => {
      const {value: newValue} = target as HTMLInputElement;
      this.dispatch({type: INPUT_VALUE_CHANGED, payload: {value: newValue}});
    };
    onKeyDown = ({key, target}: any) => {
      const {onColorChange, allowEmpty} = this.props;
      const {dispatch} = this;
      const {_formattedValue: oldValue} = this.state;
      const {value: newValue} = target as HTMLInputElement;

      if (key === 'Enter') {
        const trimmedValue = newValue.trim();
        if (allowEmpty && trimmedValue === '') {
          dispatch({
            type: VALID_VALUE_ENTERED,
            payload: {value: trimmedValue},
          });

          if (onColorChange && trimmedValue !== oldValue) {
            onColorChange(trimmedValue);
          }
        } else if (newValue && isValidColor(newValue)) {
          // Note that we want to format the color value to catch cases like oldValue === #F90 && newValue === F90. In that case we
          // _don't_ want to call onColorChange. We do however want to re-prettify the internal state value to ensure that something like F90
          // gets a # character before it.
          const formattedValue = formatColorValue(newValue, this.props);
          dispatch({
            type: VALID_VALUE_ENTERED,
            payload: {value: formattedValue},
          });

          if (onColorChange && formattedValue !== oldValue) {
            onColorChange(formattedValue);
          }
        } else {
          // We do NOT want to format the newValue here to ensure that user is not interupted while typing.
          dispatch({type: INVALID_VALUE_ENTERED, payload: {value: newValue}});
        }
      }
    };

    render() {
      const {onFocus, onBlur, onChange, onKeyDown} = this;
      const {placeholder, ...rest} = this.props;
      return (
        // @ts-expect-error - TS2322 - Type 'Omit<Readonly<Props & TProps> & Readonly<{ children?: ReactNode; }>, "placeholder"> & { onFocus: () => void; onBlur: () => void; ... 7 more ...; value?: string | undefined; }' is not assignable to type 'IntrinsicAttributes & TProps'.
        <Base
          {...rest}
          {...this.state}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder || 'Type in a color value'}
        />
      );
    }
  };

export const ColorInput = enhanceInput(({value, ...rest}: any) => (
  <TextInput {...rest} value={value} />
));
